import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Plus, Edit2, Trash2, BookOpen, Eye, EyeOff, X, Save } from 'lucide-react';
import LessonManager from './LessonManager';

const EMPTY_COURSE = {
  slug: '', title: '', subtitle: '', tagline: '', description: '',
  what_youll_learn: [], author_name: '', author_bio: '', cover_image: '',
  is_published: true,
};

export default function CourseManager() {
  const [courses, setCourses] = useState([]);
  const [editing, setEditing] = useState(null); // course object or 'new'
  const [managingLessonsFor, setManagingLessonsFor] = useState(null);

  const load = async () => {
    const { data } = await api.get('/admin/courses');
    setCourses(data || []);
  };
  useEffect(() => { load(); }, []);

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete "${c.title}" and all its lessons? This cannot be undone.`)) return;
    await api.delete(`/admin/courses/${c.id}`);
    load();
  };

  if (managingLessonsFor) {
    return <LessonManager course={managingLessonsFor} onBack={() => { setManagingLessonsFor(null); load(); }} />;
  }

  return (
    <div data-testid="admin-courses">
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="text-[11px] tracking-[0.18em] uppercase text-muted-kc">Course library</div>
          <div className="font-display uppercase text-2xl mt-1">{courses.length} {courses.length === 1 ? 'course' : 'courses'}</div>
        </div>
        <button onClick={() => setEditing({ ...EMPTY_COURSE })} className="h-11 px-5 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold hover:bg-[color:var(--kc-gold-2)] transition-colors" data-testid="new-course-btn">
          <Plus size={16}/> New Course
        </button>
      </div>

      <div className="space-y-3">
        {courses.map((c) => (
          <div key={c.id} className="bg-white border border-kc rounded-[var(--kc-radius-md)] p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid={`course-row-${c.slug}`}>
            {c.cover_image && <img src={c.cover_image} alt={c.title} className="w-20 h-28 object-cover rounded-[var(--kc-radius-sm)] flex-shrink-0" />}
            <div className="flex-1 min-w-0">
              <div className="text-[11px] tracking-[0.18em] uppercase text-muted-kc">{c.subtitle || '—'}</div>
              <div className="font-display uppercase text-2xl mt-1">{c.title}</div>
              <div className="text-sm text-muted-kc mt-1 line-clamp-2">{c.description}</div>
              <div className="mt-2 flex flex-wrap gap-2 text-xs">
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-surface-2"><BookOpen size={11}/> {c.lesson_count} lessons</span>
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-surface-2">{c.is_published ? <><Eye size={11}/> Published</> : <><EyeOff size={11}/> Hidden</>}</span>
                <span className="px-2 py-1 rounded-full bg-surface-2 font-mono">/{c.slug}</span>
              </div>
            </div>
            <div className="flex flex-row md:flex-col gap-2 flex-shrink-0">
              <button onClick={() => setManagingLessonsFor(c)} className="h-10 px-4 inline-flex items-center gap-1.5 rounded-[var(--kc-radius-sm)] bg-ink text-white text-sm font-semibold hover:bg-black/90 transition-colors" data-testid={`manage-lessons-${c.slug}`}>
                <BookOpen size={14}/> Lessons
              </button>
              <button onClick={() => setEditing(c)} className="h-10 px-4 inline-flex items-center gap-1.5 rounded-[var(--kc-radius-sm)] border border-kc-strong text-sm font-semibold hover:bg-surface-2 transition-colors" data-testid={`edit-course-${c.slug}`}>
                <Edit2 size={14}/> Edit
              </button>
              <button onClick={() => handleDelete(c)} className="h-10 px-4 inline-flex items-center gap-1.5 rounded-[var(--kc-radius-sm)] border border-kc-strong text-[color:var(--kc-danger)] text-sm font-semibold hover:bg-red-50 transition-colors" data-testid={`delete-course-${c.slug}`}>
                <Trash2 size={14}/> Delete
              </button>
            </div>
          </div>
        ))}
        {courses.length === 0 && (
          <div className="text-center py-12 bg-surface-2 rounded-[var(--kc-radius-md)] text-muted-kc">
            No courses yet. Create your first one.
          </div>
        )}
      </div>

      {editing && <CourseEditor initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function CourseEditor({ initial, onClose, onSaved }) {
  const isNew = !initial.id;
  const [form, setForm] = useState({
    ...EMPTY_COURSE,
    ...initial,
    what_youll_learn: initial.what_youll_learn || [],
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [learnInput, setLearnInput] = useState('');

  const set = (k, v) => setForm({ ...form, [k]: v });

  const save = async () => {
    setSaving(true); setErr('');
    try {
      if (isNew) {
        await api.post('/admin/courses', form);
      } else {
        await api.put(`/admin/courses/${initial.id}`, form);
      }
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const addLearn = () => {
    if (!learnInput.trim()) return;
    set('what_youll_learn', [...(form.what_youll_learn || []), learnInput.trim()]);
    setLearnInput('');
  };
  const rmLearn = (i) => set('what_youll_learn', form.what_youll_learn.filter((_, idx) => idx !== i));

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="course-editor-modal">
      <div className="bg-white rounded-[var(--kc-radius-md)] w-full max-w-2xl my-8">
        <div className="flex items-center justify-between p-5 border-b border-kc sticky top-0 bg-white rounded-t-[var(--kc-radius-md)] z-10">
          <div className="font-display uppercase text-2xl">{isNew ? 'New Course' : 'Edit Course'}</div>
          <button onClick={onClose} className="p-1 text-muted-kc" data-testid="ce-close"><X size={20}/></button>
        </div>
        <div className="p-5 space-y-4">
          <Row label="Slug (URL)" hint="Used in URLs, e.g. /courses/your-slug">
            <input value={form.slug} onChange={(e) => set('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))} className="kc-input" data-testid="ce-slug" />
          </Row>
          <Row label="Title">
            <input value={form.title} onChange={(e) => set('title', e.target.value)} className="kc-input" data-testid="ce-title" />
          </Row>
          <Row label="Subtitle" hint="Small text above title">
            <input value={form.subtitle} onChange={(e) => set('subtitle', e.target.value)} className="kc-input" data-testid="ce-subtitle" />
          </Row>
          <Row label="Tagline">
            <input value={form.tagline} onChange={(e) => set('tagline', e.target.value)} className="kc-input" data-testid="ce-tagline" />
          </Row>
          <Row label="Cover image URL">
            <input value={form.cover_image || ''} onChange={(e) => set('cover_image', e.target.value)} placeholder="https://..." className="kc-input" data-testid="ce-cover" />
            {form.cover_image && <img src={form.cover_image} alt="preview" className="mt-2 w-24 h-32 object-cover rounded-[var(--kc-radius-sm)] border border-kc"/>}
          </Row>
          <Row label="Description">
            <textarea rows={4} value={form.description} onChange={(e) => set('description', e.target.value)} className="kc-input" data-testid="ce-desc" />
          </Row>
          <Row label="What you'll learn (bullets)">
            <div className="space-y-2">
              {(form.what_youll_learn || []).map((p, i) => (
                <div key={i} className="flex gap-2 items-center bg-surface-2 px-3 py-2 rounded-[var(--kc-radius-sm)] text-sm">
                  <span className="flex-1">{p}</span>
                  <button onClick={() => rmLearn(i)} className="text-[color:var(--kc-danger)]"><X size={14}/></button>
                </div>
              ))}
              <div className="flex gap-2">
                <input value={learnInput} onChange={(e) => setLearnInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addLearn(); } }} placeholder="Add a learning outcome…" className="kc-input flex-1" data-testid="ce-learn-input" />
                <button onClick={addLearn} className="h-11 px-4 rounded-[var(--kc-radius-sm)] bg-ink text-white text-sm font-semibold" data-testid="ce-learn-add">Add</button>
              </div>
            </div>
          </Row>
          <Row label="Author name">
            <input value={form.author_name} onChange={(e) => set('author_name', e.target.value)} className="kc-input" data-testid="ce-author-name" />
          </Row>
          <Row label="Author bio">
            <textarea rows={3} value={form.author_bio} onChange={(e) => set('author_bio', e.target.value)} className="kc-input" data-testid="ce-author-bio" />
          </Row>
          <Row label="Published">
            <button type="button" onClick={() => set('is_published', !form.is_published)} className="flex items-center gap-3 text-sm font-semibold" data-testid="ce-published">
              <span className={`relative w-11 h-6 rounded-full transition-colors ${form.is_published ? 'bg-[color:var(--kc-gold)]' : 'bg-gray-300'}`}>
                <span className={`absolute top-0.5 ${form.is_published ? 'left-6' : 'left-0.5'} w-5 h-5 bg-white rounded-full shadow transition-all`} />
              </span>
              {form.is_published ? 'Published — visible to students' : 'Hidden — draft only'}
            </button>
          </Row>
          {err && <div className="text-sm text-[color:var(--kc-danger)]" data-testid="ce-error">{err}</div>}
        </div>
        <div className="p-5 border-t border-kc flex justify-end gap-2 sticky bottom-0 bg-white rounded-b-[var(--kc-radius-md)]">
          <button onClick={onClose} className="h-11 px-4 rounded-[var(--kc-radius-sm)] hover:bg-surface-2 transition-colors" data-testid="ce-cancel">Cancel</button>
          <button onClick={save} disabled={saving || !form.slug || !form.title} className="h-11 px-5 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold hover:bg-[color:var(--kc-gold-2)] disabled:opacity-50 transition-colors" data-testid="ce-save">
            <Save size={16}/> {saving ? 'Saving…' : 'Save course'}
          </button>
        </div>
      </div>
      <style>{`.kc-input { width: 100%; padding: 0.625rem 0.75rem; border: 1px solid var(--kc-border-strong); border-radius: var(--kc-radius-sm); font-size: 0.95rem; outline: none; }
      .kc-input:focus { border-color: var(--kc-gold); box-shadow: 0 0 0 2px var(--kc-gold-soft); }`}</style>
    </div>
  );
}

const Row = ({ label, hint, children }) => (
  <div>
    <label className="block text-xs font-semibold uppercase tracking-wider mb-1">{label}</label>
    {hint && <div className="text-xs text-muted-kc mb-1">{hint}</div>}
    {children}
  </div>
);
